import React, { useCallback, useMemo } from "react";
import classnames from "classnames";
import { FilterControl, useFilterBar } from "./filter-bar";
import styles from "./filter-bar.module.scss";
import lang from "translations";

const itemValue = (item) => (typeof item === "object" ? item?.value : item);

const getMultiSummary = (value, allLabel) => {
  const selectedItems = Array.isArray(value) ? value : [];
  if (!selectedItems.length) return allLabel;
  const [first, ...rest] = selectedItems;
  const firstLabel = typeof first === "object" ? first.text : first;
  return rest.length ? `${firstLabel} +${rest.length}` : firstLabel;
};

export const FilterSingleSelect = ({
  align,
  disabled = false,
  disableWhenEmpty = true,
  emptyLabel,
  id,
  label,
  loading = false,
  onChange,
  options = [],
  summaryFallback,
  value,
}) => {
  const { closeFilter } = useFilterBar();
  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  // No real choices are available when only the fallback "All" option remains.
  const isEmpty = options.length <= 1;
  const summary =
    (isEmpty && emptyLabel) || selectedOption?.text || summaryFallback || label;

  return (
    <FilterControl
      id={id}
      label={label}
      summary={summary}
      menuClassName={styles.selectMenu}
      align={align}
      disabled={disabled || (disableWhenEmpty && isEmpty)}
      loading={loading}
    >
      {options.map((option, index) => (
        <button
          key={`${option.value ?? "all"}-${index}`}
          type="button"
          className={classnames(styles.menuItem, {
            [styles.menuItemActive]: option.value === value,
            [styles.menuItemMuted]: option.disabled,
          })}
          disabled={option.disabled}
          onClick={() => {
            onChange(option.value);
            closeFilter();
          }}
        >
          {option.text}
        </button>
      ))}
    </FilterControl>
  );
};

export const FilterMultiSelect = ({
  align,
  allLabel,
  disabled = false,
  disableWhenEmpty = true,
  emptyLabel,
  hasMore = false,
  icon,
  id,
  label,
  loading = false,
  loadingMore = false,
  onChange,
  onLoadMore,
  options = [],
  value,
}) => {
  const selectedItems = useMemo(() => (Array.isArray(value) ? value : []), [value]);
  const selectedValues = useMemo(() => selectedItems.map(itemValue), [selectedItems]);
  const allSelected = Array.isArray(value) && !value.length;

  const toggleOption = useCallback(
    (option) => {
      const selected = selectedValues.includes(option.value);
      const nextValue = selected
        ? selectedItems.filter((item) => itemValue(item) !== option.value)
        : [...selectedItems, option];
      onChange(nextValue.length === options.length ? [] : nextValue.length ? nextValue : null);
    },
    [onChange, options.length, selectedItems, selectedValues],
  );

  const onMenuScroll = useCallback(
    (event) => {
      if (!onLoadMore || !hasMore || loading || loadingMore) return;
      const target = event.currentTarget;
      const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
      if (distanceToBottom <= 32) {
        onLoadMore();
      }
    },
    [hasMore, loading, loadingMore, onLoadMore],
  );

  return (
    <FilterControl
      id={id}
      label={label}
      icon={icon}
      summary={getMultiSummary(value, allLabel)}
      loading={loading}
      disabled={disabled || (disableWhenEmpty && !options.length)}
      menuClassName={styles.multiSelectMenu}
      align={align}
      onMenuScroll={onMenuScroll}
    >
      <button
        type="button"
        className={classnames(styles.menuItem, {
          [styles.menuItemActive]: allSelected,
        })}
        onClick={() => onChange([])}
      >
        {allLabel}
      </button>
      {!options.length && !loading && (
        <div className={classnames(styles.menuItem, styles.menuItemMuted)}>
          {emptyLabel || allLabel}
        </div>
      )}
      {options.map((option, index) => {
        const selected = selectedValues.includes(option.value);
        return (
          <button
            key={`${option.value}-${index}`}
            type="button"
            className={classnames(styles.menuItem, {
              [styles.menuItemActive]: selected,
              [styles.menuItemMuted]: option.disabled,
            })}
            disabled={option.disabled}
            onClick={() => toggleOption(option)}
          >
            <span className={classnames(styles.checkbox, { [styles.checkboxActive]: selected })} />
            {option.text}
          </button>
        );
      })}
      {loadingMore && (
        <div className={classnames(styles.menuItem, styles.menuItemMuted)}>{lang.loading}</div>
      )}
    </FilterControl>
  );
};
